import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getQuotes, getQuote, acceptQuote, declineQuote, setCurrentQuoteComments } from '../redux/slices/quote';
import { showAlert } from '../redux/slices/alerts';
import { fetchAccountUsers } from '../redux/slices/users';
import {
    Container,
    Typography,
    Box,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Alert as MuiAlert,
    Tab,
    Pagination,
} from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { AccessTime, CalendarToday, ExpandMore, ExpandLess, Download } from '@mui/icons-material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import styled from '@emotion/styled';
import MarkdownDisplay from '../components/MarkdownDisplay';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import CommentThread from '../components/CommentThread';
import PageHeader from '../components/PageHeader';
import { statusColor } from '../utils/statusColors';
import { formatDayShort, serverDayOf } from '../utils/dateUtils';

const PageContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(2),
    width: '100%',
}));

const QuoteCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== '$terminal',
})(({ theme, $terminal }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: $terminal ? 'default' : 'pointer',
    '&:hover': {
        backgroundColor: $terminal ? undefined : theme.palette.action.hover,
    },
}));

const StatusChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== '$status',
})(({ theme, $status }) => {
    const bg = statusColor(theme, 'quote', $status);
    return { backgroundColor: bg, color: theme.palette.getContrastText(bg), fontWeight: theme.typography.fontWeightSemiBold, borderRadius: '16px' };
});

const MetaGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr 1fr',
    },
}));

const NotesBox = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const ScopeList = styled("ul")(({ theme }) => ({
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
    "& li": {
        marginBottom: theme.spacing(0.5),
    },
}));

const SummaryMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
}));

const MetaItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
}));

const ActionRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

const LineItemsTable = styled(Table)(({ theme }) => ({
    borderCollapse: 'collapse',
    '& .MuiTableCell-root': {
        padding: theme.spacing(0.75, 1.5),
    },
}));

const StatusAlert = styled(MuiAlert)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const DetailDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const TabsWrapper = styled(Box)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
}));

const PaginationBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
}));

const HeaderAction = styled(Button)(({ theme }) => ({
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}));

const PriceSummaryBox = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const PriceSummaryRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const CommentsSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    cursor: 'default',
}));

const CommentsLabel = styled(Typography)(({ theme }) => ({
    display: 'block',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
}));

const TERMINAL_STATUSES = ['Accepted', 'Declined', 'Rejected'];
const POST_SENT_STATUSES = ['Sent', 'Accepted', 'Declined', 'Rejected'];

const ClientQuotes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { quoteId: paramQuoteId } = useParams();

    const quotes = useSelector((state) => state.quote.quotes);
    const currentQuote = useSelector((state) => state.quote.currentQuote);
    const accountUsersAccountId = useSelector((state) => state.accountUsers.accountId);

    const [selectedQuoteId, setSelectedQuoteId] = useState(null);
    const [isAccepting, setIsAccepting] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [tabValue, setTabValue] = useState('sent');
    const [currentPage, setCurrentPage] = useState(1);
    const [quoteRequestOpen, setQuoteRequestOpen] = useState(false);
    const itemsPerPage = 25;

    const CLIENT_TAB_GROUPS = {
        sent: ['Sent'],
        inprogress: ['Pending', 'Draft', 'Under Review'],
        approved: ['Accepted'],
        rejected: ['Declined', 'Rejected'],
    };

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
        setCurrentPage(1);
        setSelectedQuoteId(null);
    };

    const tabBadgeLabel = (label, tab) => {
        const count = quotes.filter((q) => CLIENT_TAB_GROUPS[tab].includes(q.status)).length;
        return count > 0 ? `${label} (${count})` : label;
    };

    const filteredByTab = quotes.filter((q) => CLIENT_TAB_GROUPS[tabValue].includes(q.status));
    const totalPages = Math.ceil(filteredByTab.length / itemsPerPage);
    const pagedQuotes = filteredByTab.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // Load quotes list on mount
    useEffect(() => {
        dispatch(getQuotes({}));
    }, [dispatch]);

    // Auto-select from URL param on mount
    useEffect(() => {
        if (paramQuoteId) {
            setSelectedQuoteId(paramQuoteId);
            dispatch(getQuote({ quoteId: paramQuoteId }));
        }
    }, [paramQuoteId, dispatch]);

    const quoteAccountId = currentQuote && currentQuote._id === selectedQuoteId ? currentQuote.account?._id || currentQuote.account || null : null;
    useEffect(() => {
        if (quoteAccountId && quoteAccountId !== accountUsersAccountId) {
            dispatch(fetchAccountUsers({ accountId: quoteAccountId }));
        }
    }, [quoteAccountId, accountUsersAccountId, dispatch]);

    // Reset loading flags if quote selection changes (saga error fallback)
    useEffect(() => {
        setIsAccepting(false);
        setIsDeclining(false);
    }, [selectedQuoteId]);

    const handleSelectQuote = (quote) => {
        setSelectedQuoteId(quote._id);
        dispatch(getQuote({ quoteId: quote._id }));
    };

    const handleAccept = () => {
        setIsAccepting(true);
        dispatch(
            acceptQuote({
                quoteId: selectedQuoteId,
                callback: (response) => {
                    setIsAccepting(false);
                    if (!response) return;
                    const { project } = response;
                    navigate(`/account/${project.account.slug}/project/${project.id || project._id}`);
                },
            })
        );
    };

    const handleDecline = () => {
        const quoteId = selectedQuoteId;
        setIsDeclining(true);
        dispatch(
            declineQuote({
                quoteId,
                callback: (response) => {
                    setIsDeclining(false);
                    if (!response) return;
                    dispatch(getQuotes({}));
                    dispatch(getQuote({ quoteId }));
                    dispatch(showAlert({ severity: 'info', message: 'You have declined this quote.' }));
                },
            })
        );
    };

    const formatDate = (dateStr) => formatDayShort(dateStr) || '—';

    const handleCommentsChanged = (quoteId) => (nextComments) => {
        dispatch(setCurrentQuoteComments({ quoteId, comments: nextComments }));
    };



    const computeProjectedDates = (quote) => {
        if (!quote?.projectedStartDate || !quote?.projectedDueDate) return null;
        return { expectedStart: quote.projectedStartDate, expectedDue: quote.projectedDueDate };
    };

    const renderActionSection = (quote) => {
        const { status } = quote;

        if (status === 'Sent') {
            return (
                <>
                    <StatusAlert severity="info">
                        Your quote is ready for review. Total: {quote.totalHours} hours.
                    </StatusAlert>
                    <ActionRow>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={isAccepting || isDeclining}
                            onClick={handleAccept}
                            startIcon={isAccepting ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isAccepting ? 'Accepting…' : 'Accept Quote'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            disabled={isAccepting || isDeclining}
                            onClick={handleDecline}
                            startIcon={isDeclining ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {isDeclining ? 'Declining…' : 'Decline'}
                        </Button>
                    </ActionRow>
                </>
            );
        }

        if (status === 'Accepted') {
            const projectSlug = quote.project?.account?.slug || '';
            const projectId = quote.project?._id || quote.project?.id || '';
            return (
                <>
                    <StatusAlert severity="success">
                        You accepted this quote. Project has been created.
                    </StatusAlert>
                    {projectSlug && projectId && (
                        <ActionRow>
                            <Button variant="contained" color="primary" onClick={() => navigate(`/account/${projectSlug}/project/${projectId}`)}>
                                View Project
                            </Button>
                        </ActionRow>
                    )}
                </>
            );
        }

        if (status === 'Declined') {
            return (
                <StatusAlert severity="info">
                    You declined this quote.
                </StatusAlert>
            );
        }

        if (status === 'Rejected') {
            return (
                <StatusAlert severity="warning">
                    This quote was not approved by our team. Please contact us to discuss your requirements.
                </StatusAlert>
            );
        }

        if (status === 'Pending') {
            return (
                <StatusAlert severity="info">
                    Your quote request has been received. We are working on it.
                </StatusAlert>
            );
        }

        // Draft or Under Review
        return (
            <StatusAlert severity="info">
                Your quote is being prepared by our team. You will be notified when it is ready.
            </StatusAlert>
        );
    };

    const isDetailVisible = selectedQuoteId && currentQuote && currentQuote._id === selectedQuoteId;

    return (
        <PageContainer maxWidth="lg">
            <PageHeader
                title="My Quotes"
                subtitle="Review and manage your project quotes"
                actions={
                    <HeaderAction
                        variant="contained"
                        color="primary"
                        startIcon={<RequestQuoteIcon />}
                        onClick={() => setQuoteRequestOpen(true)}
                    >
                        Request a Quote
                    </HeaderAction>
                }
            />

            <TabContext value={tabValue}>
                <TabsWrapper>
                    <TabList onChange={handleTabChange} aria-label="my quotes tabs">
                        <Tab label={tabBadgeLabel('Ready for Review', 'sent')} value="sent" />
                        <Tab label={tabBadgeLabel('In Progress', 'inprogress')} value="inprogress" />
                        <Tab label={tabBadgeLabel('Approved', 'approved')} value="approved" />
                        <Tab label={tabBadgeLabel('Rejected', 'rejected')} value="rejected" />
                    </TabList>
                </TabsWrapper>

                {pagedQuotes.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No quotes found.
                    </Typography>
                )}

                {pagedQuotes.map((quote) => {
                const isTerminal = TERMINAL_STATUSES.includes(quote.status);
                return (
                    <QuoteCard
                        key={quote._id}
                        elevation={selectedQuoteId === quote._id ? 3 : 1}
                        $terminal={isTerminal}
                        onClick={() => handleSelectQuote(quote)}
                    >
                        {/* Title row: bold name + status chip + expand icon */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body1" fontWeight="bold">
                                    {quote.name || quote.projectType || 'Quote'}
                                </Typography>
                                <StatusChip label={quote.status} size="small" $status={quote.status} />
                            </Box>
                            {!isTerminal && (
                                selectedQuoteId === quote._id
                                    ? <ExpandLess fontSize="small" style={{ color: 'rgba(0,0,0,0.54)' }} />
                                    : <ExpandMore fontSize="small" style={{ color: 'rgba(0,0,0,0.54)' }} />
                            )}
                        </Box>
                        {/* Description */}
                        {quote.description && (
                            <MarkdownDisplay content={quote.description || null} singleLine={selectedQuoteId===quote._id ? false : true} />
                        )}
                        {/* Icon meta row */}
                        <SummaryMetaRow>
                            {POST_SENT_STATUSES.includes(quote.status) && quote.totalHours != null && (
                                <MetaItem>
                                    <AccessTime color="primary" fontSize="small" />
                                    <Typography color="primary" variant="caption" fontWeight="bold">{quote.totalHours} hrs</Typography>
                                </MetaItem>
                            )}
                            <MetaItem>
                                <CalendarToday fontSize="small" />
                                <Typography variant="caption">{formatDayShort(serverDayOf(quote.createdAt)) || '—'}</Typography>
                            </MetaItem>
                        </SummaryMetaRow>

                        {/* Detail view inline under the selected card */}
                        {isDetailVisible && selectedQuoteId === quote._id && (
                            <>
                                <DetailDivider />
                                <MetaGrid>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Project Type</Typography>
                                        <Typography variant="body1">{currentQuote.projectType || '—'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Project Name</Typography>
                                        <Typography variant="body1">{currentQuote.name || '—'}</Typography>
                                    </Box>
                                    {POST_SENT_STATUSES.includes(currentQuote.status) && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
                                            <Typography variant="body1">{currentQuote.totalHours != null ? currentQuote.totalHours : '—'}</Typography>
                                        </Box>
                                    )}
                                    {POST_SENT_STATUSES.includes(currentQuote.status) && currentQuote.rush && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Rush</Typography>
                                            <Typography variant="body1">Yes</Typography>
                                        </Box>
                                    )}
                                    {currentQuote.sentAt && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Sent</Typography>
                                            <Typography variant="body1">{formatDate(currentQuote.sentAt)}</Typography>
                                        </Box>
                                    )}
                                </MetaGrid>
                                {POST_SENT_STATUSES.includes(currentQuote.status) && currentQuote.quotePdf && (
                                    <Box mb={1}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Download />}
                                            href={`/api/attachment/${currentQuote.quotePdf._id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Download quote PDF
                                        </Button>
                                    </Box>
                                )}
                                {POST_SENT_STATUSES.includes(currentQuote.status) && currentQuote.notes && (
                                    <Box mt={1} mb={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Notes
                                        </Typography>
                                        <NotesBox>
                                            <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}>{currentQuote.notes}</Markdown>
                                        </NotesBox>
                                    </Box>
                                )}

                                {POST_SENT_STATUSES.includes(currentQuote.status) && [
                                    ['Assumptions', currentQuote.assumptions],
                                    ['Inclusions', currentQuote.inclusions],
                                    ['Exclusions', currentQuote.exclusions],
                                ].map(([title, items]) => (
                                    Array.isArray(items) && items.length > 0 ? (
                                        <Box key={title} mt={1} mb={1}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                {title}
                                            </Typography>
                                            <ScopeList>
                                                {items.map((text, i) => (
                                                    <li key={i}><Typography variant="body2" component="span">{text}</Typography></li>
                                                ))}
                                            </ScopeList>
                                        </Box>
                                    ) : null
                                ))}

                                {(() => {
                                    const projected = computeProjectedDates(currentQuote);
                                    if (!projected && !currentQuote.dueDate) return null;
                                    return (
                                        <Box mt={1} mb={1}>
                                            {currentQuote.dueDate && (
                                                <Typography variant="body2" gutterBottom={!!projected}>
                                                    <strong>Due date:</strong> {formatDate(currentQuote.dueDate)}
                                                </Typography>
                                            )}
                                            {projected && (
                                                <>
                                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                                        If accepted today —
                                                    </Typography>
                                                    <Box display="flex" gap={3} flexWrap="wrap">
                                                        <Typography variant="body2">
                                                            <strong>Start:</strong> {formatDate(projected.expectedStart)}
                                                        </Typography>
                                                        {!currentQuote.dueDate && (
                                                            <Typography variant="body2">
                                                                <strong>Est. Completion:</strong> {formatDate(projected.expectedDue)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    );
                                })()}

                                {POST_SENT_STATUSES.includes(currentQuote.status) && Array.isArray(currentQuote.lineItems) && currentQuote.lineItems.length > 0 && (
                                    <LineItemsTable size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Task</strong></TableCell>
                                                <TableCell align="right"><strong>Hours</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentQuote.lineItems.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{item.title || '—'}</TableCell>
                                                    <TableCell align="right">{item.hours}</TableCell>
                                                </TableRow>
                                            ))}
                                            {currentQuote.contingencyHours > 0 && (
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Contingency ({currentQuote.contingencyRate != null ? `${Math.round(currentQuote.contingencyRate * 100)}%` : '10%'})
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body2" color="text.secondary">
                                                            {currentQuote.contingencyHours}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            <TableRow>
                                                <TableCell><strong>Total</strong></TableCell>
                                                <TableCell align="right"><strong>{currentQuote.totalHours}</strong></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </LineItemsTable>
                                )}

                                {POST_SENT_STATUSES.includes(currentQuote.status) && currentQuote.lockedRate != null && (
                                    <PriceSummaryBox>
                                        {(() => {
                                            const subtotalHours = (currentQuote.lineItems || []).reduce((s, i) => s + i.hours, 0);
                                            const contingencyHours = currentQuote.contingencyHours || 0;
                                            const totalHours = subtotalHours + contingencyHours;
                                            const rate = currentQuote.lockedRate || 0;
                                            const totalGross = totalHours * rate;
                                            let discountAmount = 0;
                                            if (currentQuote.discountValue > 0) {
                                                discountAmount = currentQuote.discountType === 'percent'
                                                    ? totalGross * (currentQuote.discountValue / 100)
                                                    : currentQuote.discountValue;
                                            }
                                            const totalPrice = totalGross - discountAmount;
                                            const fmt = (n) => `$${n.toFixed(2)}`;
                                            return (
                                                <>
                                                    <PriceSummaryRow>
                                                        <Typography variant='body2' color='text.secondary'>Subtotal ({subtotalHours} hrs × ${rate}/hr)</Typography>
                                                        <Typography variant='body2'>{fmt(subtotalHours * rate)}</Typography>
                                                    </PriceSummaryRow>
                                                    {contingencyHours > 0 && (
                                                        <PriceSummaryRow>
                                                            <Typography variant='body2' color='text.secondary'>Contingency ({contingencyHours} hrs × ${rate}/hr)</Typography>
                                                            <Typography variant='body2'>{fmt(contingencyHours * rate)}</Typography>
                                                        </PriceSummaryRow>
                                                    )}
                                                    <PriceSummaryRow>
                                                        <Typography variant='body2' color='text.secondary'>Total Hours</Typography>
                                                        <Typography variant='body2'>{totalHours} hrs</Typography>
                                                    </PriceSummaryRow>
                                                    {discountAmount > 0 && (
                                                        <PriceSummaryRow>
                                                            <Typography variant='body2' color='text.secondary'>
                                                                Discount ({currentQuote.discountType === 'percent' ? `${currentQuote.discountValue}%` : 'fixed'})
                                                            </Typography>
                                                            <Typography variant='body2' color='error.main'>−{fmt(discountAmount)}</Typography>
                                                        </PriceSummaryRow>
                                                    )}
                                                    <PriceSummaryRow>
                                                        <Typography variant='body2' fontWeight='bold'>Total Price</Typography>
                                                        <Typography variant='body2' fontWeight='bold'>{fmt(totalPrice)}</Typography>
                                                    </PriceSummaryRow>
                                                </>
                                            );
                                        })()}
                                    </PriceSummaryBox>
                                )}

                                {renderActionSection(currentQuote)}

                                {Array.isArray(currentQuote.comments) && (currentQuote.status === 'Sent' || currentQuote.comments.length > 0) && (
                                    <CommentsSection onClick={(e) => e.stopPropagation()}>
                                        <DetailDivider />
                                        <CommentsLabel variant="caption" color="text.secondary">
                                            Comments
                                        </CommentsLabel>
                                        <CommentThread
                                            key={currentQuote._id}
                                            parentType="quote"
                                            parentId={currentQuote._id}
                                            comments={currentQuote.comments}
                                            onChanged={handleCommentsChanged(currentQuote._id)}
                                            readOnly={currentQuote.status !== 'Sent'}
                                        />
                                    </CommentsSection>
                                )}
                            </>
                        )}
                    </QuoteCard>
                );
            })}

                {totalPages > 1 && (
                    <PaginationBox>
                        <Pagination count={totalPages} page={currentPage} onChange={(_, p) => setCurrentPage(p)} />
                    </PaginationBox>
                )}
            </TabContext>

            <QuoteRequestDialog
                open={quoteRequestOpen}
                onClose={() => setQuoteRequestOpen(false)}
                onCreated={() => {
                    dispatch(getQuotes({}));
                    setTabValue('inprogress');
                }}
            />
        </PageContainer>
    );
};

export default ClientQuotes;
